import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CustomTextField from "../components/CustomTextField";
import CustomDropdown from "../components/CustomDropdown";
import CustomButton from "../components/CustomButton";
import ReusableHeader from "./ReusableHeader";
import "../styles/layout.css";
import { toast } from "react-toastify";
import { useUsersApi } from "../api/users";
import { useOrganizationApi } from "../api/organization";
import { decodeJwt } from "../util/decodeJwt";
import { validatePassword, validatePhoneNumber } from "../util/validation";
import { applyPrimaryColor } from "../App.js";
import LogoCropper from "./LogoCropper";

export default function ProfileUpdateWidget({ onSubmit }) {
  const { getVendorUserById, updateVendorUser, registerVendorUser } = useUsersApi();
  const { getVendorOrgById, updateOrgBranding, uploadOrgLogo } = useOrganizationApi();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    password: "",
    role: "",
    status: "",
    address: "",
    organizationId: "",
  });

  // Branding state — belongs to the org, saved separately
  const [brandData, setBrandData] = useState({
    primary_color: localStorage.getItem("primaryColor") || "",
    logo: localStorage.getItem("orgLogo") || "",
  });
  const [logoPreview, setLogoPreview] = useState(localStorage.getItem("orgLogo") || "");
  const [showLogoCropper, setShowLogoCropper] = useState(false);
  const [logoModified, setLogoModified] = useState(false); // true only when user picks a NEW image

  const [loading, setLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [userId, setUserId] = useState("");
  const [currentUserRole, setCurrentUserRole] = useState("");
  const [orgId, setOrgId] = useState("");
  const [originalColor, setOriginalColor] = useState(localStorage.getItem("primaryColor") || "");
  const [originalLogo, setOriginalLogo] = useState(localStorage.getItem("orgLogo") || "");

  const originalColorRef = useRef(originalColor);
  const originalLogoRef = useRef(originalLogo);
  const isSubmittedRef = useRef(false);

  useEffect(() => {
    originalColorRef.current = originalColor;
  }, [originalColor]);

  useEffect(() => {
    originalLogoRef.current = originalLogo;
  }, [originalLogo]);

  // Track if submitted to prevent unmount effects if any (though we removed them)
  useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, []);

  // Decode JWT
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = decodeJwt(token);
        if (decoded?._id) setUserId(decoded._id);
        if (decoded?.role) setCurrentUserRole(decoded.role.toLowerCase());
        setOrgId(decoded.organizationId || "");
      } catch (err) {
        console.error("JWT decode failed", err);
      }
    }
  }, []);

  // Role options
  const getRoleOptions = async () => {
    let targetUserRole = null;
    if (isEditMode) {
      const user = await getVendorUserById(id);
      targetUserRole = user?.role?.toLowerCase();
    }
    const loggedInRole = currentUserRole?.toLowerCase();
    switch (loggedInRole) {
      case "admin":
        return [{ value: "admin", label: "Admin" }];
      case "manufacturer":
        if (targetUserRole === "manufacturer")
          return [{ value: "manufacturer", label: "Manufacturer" }];
        break;
      case "distributor":
        if (targetUserRole === "distributor")
          return [{ value: "distributor", label: "Distributor" }];
        break;
      case "dealer":
        if (targetUserRole === "dealer")
          return [{ value: "dealer", label: "Dealer" }];
        break;
      default:
        return [];
    }
    return [];
  };

  useEffect(() => {
    (async () => {
      if (currentUserRole) {
        const options = await getRoleOptions();
        setRoleOptions(options);
        if (options.length === 1 && !formData.role) {
          setFormData((prev) => ({ ...prev, role: options[0].value }));
        }
      }
    })();
  }, [currentUserRole, id, isEditMode]);

  // Load org (for branding + org dropdown)
  useEffect(() => {
    async function loadOrganizations() {
      if (!userId || !currentUserRole) return;
      if (!orgId) return;

      try {
        const result = await getVendorOrgById(orgId);
        setFormData((prev) => ({ ...prev, organizationId: result._id }));

        // Load stored branding from org
        setBrandData({
          primary_color: result.primary_color || "",
          logo: result.logo || "",
        });
        setLogoPreview(result.logo || "");
        setOriginalColor(result.primary_color || localStorage.getItem("primaryColor") || "");
        setOriginalLogo(result.logo || localStorage.getItem("orgLogo") || "");
      } catch (err) {
        console.error("Failed to load org", err);
        toast.error("Failed to load organization");
      }
    }
    loadOrganizations();
  }, [userId, currentUserRole]);

  // Load user data if editing
  useEffect(() => {
    async function loadUser() {
      if (!isEditMode) return;
      try {
        setLoading(true);
        const user = await getVendorUserById(id);
        const isAdminUser = user.role?.toLowerCase() === "admin";
        setFormData({
          name: user.name || "",
          phoneNumber: user.phoneNumber || "",
          password: "",
          role: user.role || "",
          status: user.status || "active",
          address: user.organization?.address || "",
          organizationId: user.organization?._id || user.organizationId || "",
        });
        if (isAdminUser) setCurrentUserRole("admin");
      } catch (err) {
        console.error(err);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, [id, isEditMode]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleBrandChange = (key, value) => {
    setBrandData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const submitData = { ...formData };
    delete submitData.role;

    if (!formData.name?.trim()) { toast.error("Name is required"); return; }
    if (!formData.phoneNumber?.trim()) { toast.error("Phone number is required"); return; }
    if (!validatePhoneNumber(formData.phoneNumber)) { toast.error("Phone number must be 10 digits"); return; }

    if (!isEditMode && !formData.password?.trim()) {
      toast.error("Password is required"); return;
    }
    if (formData.password?.trim() && !validatePassword(formData.password)) {
      toast.error("Password must be 8+ characters with at least 1 letter and 1 number"); return;
    }
    if (!formData.address?.trim()) { toast.error("Address is required"); return; }
    if (currentUserRole !== "admin" && !formData.organizationId) {
      toast.error("Please select organization"); return;
    }

    // Ensure logo is not empty if the user can edit branding
    const isBrandingEditable = currentUserRole === "manufacturer" || currentUserRole === "admin";
    if (isBrandingEditable && !brandData.logo) {
      toast.error("Brand Logo is required");
      return;
    }

    try {
      setLoading(true);

      // 1. Save user profile
      let result;
      if (isEditMode) {
        result = await updateVendorUser(id, submitData);
        toast.success("Profile updated successfully!");
      } else {
        result = await registerVendorUser(submitData);
        toast.success("User registered successfully!");
      }

      // 2. Save branding to org (manufacturer + admin) — isolated so it never blocks navigation
      if ((currentUserRole === "manufacturer" || currentUserRole === "admin") && orgId) {
        try {
          let logoValue = brandData.logo;

          // Only upload to server if user picked a NEW image this session (prevents duplicates)
          if (logoModified && logoValue && logoValue.startsWith("data:")) {
            try {
              const uploadedUrl = await uploadOrgLogo(orgId, logoValue);
              logoValue = uploadedUrl || logoValue;
            } catch (uploadErr) {
              console.warn("Logo upload to server failed, storing base64 directly:", uploadErr);
            }
          }

          // Cache locally FIRST — so logo shows even if backend call fails
          localStorage.setItem("primaryColor", brandData.primary_color);
          if (logoValue) {
            try {
              localStorage.setItem("orgLogo", logoValue);
            } catch (quotaErr) {
              console.error("localStorage quota exceeded — logo too large:", quotaErr);
            }
            setLogoPreview(logoValue);
          }
          applyPrimaryColor(brandData.primary_color);
          window.dispatchEvent(new Event("branding-updated"));

          // Persist to backend (non-blocking — failure won't prevent navigation)
          await updateOrgBranding(orgId, {
            primary_color: brandData.primary_color,
            logo: logoValue,
          });

          toast.success("Brand settings saved!");
        } catch (brandErr) {
          console.warn("Branding save failed (backend):", brandErr);
          // Logo already cached locally — user will still see it this session
          toast.success("Profile updated! (Branding may sync on next login)");
        }
      }

      if (onSubmit) onSubmit(result?.data || submitData);
      isSubmittedRef.current = true;
      navigate("/");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const canEditBranding = currentUserRole === "manufacturer" || currentUserRole === "admin";

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div className="user-container">
      <ReusableHeader title={isEditMode ? "Profile" : "Create User"} />

      <div className="user-form-card">

        {/* ── User fields ── */}
        <div className="user-columns">
          <div className="user-column-left">
            <CustomTextField
              label="Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
            <CustomTextField
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleChange("phoneNumber", e.target.value)}
            />
            <CustomTextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
            />
          </div>

          <div className="user-column-right">
            <CustomDropdown
              label="Role"
              options={roleOptions}
              value={formData.role}
              onChange={(value) => handleChange("role", value)}
              isMultiSelect={false}
              disabled={false}
            />
            <CustomTextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </div>
        </div>

        {/* ── Brand Settings (manufacturer only) ── */}
        {canEditBranding && (
          <div className="profile-brand-section">
            <div className="manufacturer-config-divider">Brand Settings</div>

            <div className="profile-brand-row">

              {/* Color Picker */}
              <div className="color-picker-wrapper">
                <label className="color-picker-label">Primary Color</label>
                <div className="color-picker-row">
                  <input
                    type="color"
                    value={brandData.primary_color}
                    onChange={(e) => {
                      handleBrandChange("primary_color", e.target.value);
                    }}
                    className="color-picker-input"
                    title="Pick brand primary color"
                  />
                  <span className="color-picker-value">{brandData.primary_color}</span>
                </div>
              </div>

              {/* Logo Upload */}
              <div className="logo-upload-wrapper">
                <label className="color-picker-label">Brand Logo</label>
                <div className="logo-upload-row">
                  {logoPreview ? (
                    <div className="logo-preview-box">
                      <img src={logoPreview} alt="Logo" className="logo-preview-img" />
                      <button
                        type="button"
                        className="logo-remove-btn"
                        onClick={() => { setLogoPreview(""); handleBrandChange("logo", ""); }}
                      >
                        ✕ Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="logo-upload-btn"
                      onClick={() => setShowLogoCropper(true)}
                    >
                      Upload Logo
                    </button>
                  )}
                </div>
              </div>

              {showLogoCropper && (
                <LogoCropper
                  onDone={(base64) => {
                    setLogoPreview(base64);
                    handleBrandChange("logo", base64);
                    setLogoModified(true); // mark as newly picked — triggers upload on save
                    setShowLogoCropper(false);
                  }}
                  onCancel={() => setShowLogoCropper(false)}
                />
              )}

            </div>
          </div>
        )}

        {/* ── Buttons ── */}
        <div className="user-buttons">
          <CustomButton
            label="Cancel"
            className="user-cancel"
            onClick={handleCancel}
          />
          <CustomButton
            label={loading ? (isEditMode ? "Updating..." : "Creating...") : isEditMode ? "Update" : "Create"}
            className="user-create"
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>

      </div>
    </div>
  );
}
